import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, X } from 'lucide-react'
import Button from './Button'
import Stepper from './Stepper'
import focusFirstInvalid from './focusFirstInvalid'
import { kit } from './strings'
import './FormPage.css'

/**
 * A form that gets its own screen instead of a side sheet.
 *
 * The sheets were the right shape for a two-field edit and the wrong one for
 * everything else: 560px of column for a form the legacy gives a full modal to,
 * with the list still visible behind it doing nothing except making the sheet
 * feel like a lid over the work. Anything with more than a handful of controls
 * belongs on a page.
 *
 * What the page adds beyond width:
 *
 *   A BACK affordance, and a real one — the caller drives it with navigate(-1)
 *   or a route, so the browser's own back button does the same thing. A sheet
 *   has only an X, and the browser back button under a sheet leaves the app.
 *
 *   STEPS, where the form has them. Most of these forms are three decisions
 *   wearing fourteen controls, and the legacy already knows it: several of its
 *   modals are Bootstrap wizards. One long column asks you to hold all of it at
 *   once; a step asks one question at a time and says how many are left.
 *
 *   Room to lay fields out in more than one column, which is what actually
 *   fixes "tight" — 880px is wide enough for two columns of real fields and
 *   narrow enough that a line of text is still a comfortable measure.
 *
 * The footer is sticky, so Save is reachable from anywhere in a long step
 * without scrolling to the end of it.
 *
 * @param {{id: string, label: string, hint?: string}[]} [steps]
 * @param {(index: number) => boolean} [validateStep]  false blocks the move on
 * @param {(values: unknown) => void} onSubmit
 * @param {import('react').ReactNode} [actions]  page-level controls, in the head
 * @param {boolean} [submitDisabled]  nothing to submit yet — an unchanged record, an
 *   empty required field; the button says so instead of accepting a no-op
 */
export default function FormPage({
  title,
  subtitle,
  icon: Icon,
  steps,
  step,
  onStep,
  validateStep,
  onBack,
  onSubmit,
  submitLabel,
  submitting,
  submitDisabled = false,
  actions,
  aside,
  children,
}) {
  /* Uncontrolled unless the caller wants to own it — most forms only care about
     the step to validate it, and threading state they do not read is noise. */
  const [internal, setInternal] = useState(0)
  const current = step ?? internal
  const setCurrent = onStep ?? setInternal
  const [furthest, setFurthest] = useState(current)
  const formRef = useRef(null)

  useEffect(() => {
    setFurthest((value) => Math.max(value, current))
  }, [current])

  const count = steps?.length ?? 0
  const last = count === 0 || current === count - 1

  const go = (index) => {
    /* Forward only through validation; back is always free. */
    if (index > current && validateStep && !validateStep(current)) {
      focusFirstInvalid(formRef.current)
      return
    }
    setCurrent(index)
    if (typeof document !== 'undefined') {
      document.querySelector('.formpage__body')?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const submit = (event) => {
    event.preventDefault()
    if (!last) {
      go(current + 1)
      return
    }
    if (validateStep && !validateStep(current)) {
      focusFirstInvalid(formRef.current)
      return
    }
    onSubmit?.(event)
  }

  return (
    <form className="formpage" ref={formRef} onSubmit={submit} noValidate>
      <header className="formpage__head">
        <div className="formpage__lead">
          {/* Back rather than a close X: this is a place you navigated TO, and
              the word for leaving it is the same one the browser uses. */}
          <button type="button" className="formpage__back" onClick={onBack}>
            <ArrowLeft className="rtl-flip" size={18} strokeWidth={2} />
            <span className="sr-only">{kit.back}</span>
          </button>

          {Icon && (
            <span className="formpage__icon" aria-hidden="true">
              <Icon size={20} strokeWidth={1.75} />
            </span>
          )}

          <div className="formpage__titles">
            <h1 className="formpage__title">
              <bdi>{title}</bdi>
            </h1>
            {subtitle && (
              <p className="formpage__subtitle">
                <bdi>{subtitle}</bdi>
              </p>
            )}
          </div>
        </div>

        {/* A control that belongs to the whole page rather than to one field — a view
            switch, a mode — sits in the head beside the title, where the list pages
            already keep theirs. */}
        {actions && <div className="formpage__actions">{actions}</div>}

        <button type="button" className="formpage__close" onClick={onBack} aria-label={kit.close}>
          <X size={18} strokeWidth={2} />
        </button>
      </header>

      {count > 1 && (
        <div className="formpage__rail">
          <Stepper steps={steps} current={current} furthest={furthest} onGo={go} />
        </div>
      )}

      <div className="formpage__body">
        {/* A render prop when the form has steps, so a step's fields are not
            merely hidden — they are not mounted, and a field two steps away
            cannot hold a value, take focus or fail validation off-screen. */}
        <div className="formpage__col">
          {typeof children === 'function' ? children({ step: current }) : children}
        </div>
        {aside && <div className="formpage__aside">{aside}</div>}
      </div>

      <footer className="formpage__foot">
        <div className="formpage__foot-inner">
          <Button type="button" variant="ghost" onClick={onBack}>
            {kit.cancel}
          </Button>

          <div className="formpage__moves">
            {count > 1 && current > 0 && (
              <Button type="button" variant="ghost" onClick={() => go(current - 1)}>
                {kit.previousStep}
              </Button>
            )}
            <Button type="submit" busy={last && submitting} disabled={submitting || (last && submitDisabled)}>
              {last ? submitLabel : kit.nextStep}
            </Button>
          </div>
        </div>
      </footer>
    </form>
  )
}
